import { Nullable } from 'shared_DEPRECATED/types';
import { Picture } from 'shared_DEPRECATED/types/Picture';
import { dateUtils } from 'shared_DEPRECATED/utils';
import { DAILY_STATUS_VALUES } from 'shared_DEPRECATED/utils/config';
import { firstLetterUpperCase } from 'shared_DEPRECATED/utils/string/string';
import { ObjectValues } from 'types/common';

import {
  CHALLENGE_TYPES,
  ENTITY_TYPES,
  TARGET_TYPES,
} from 'features/challenge/config';
import {
  ChallengeDifficultyRating,
  ChallengeGoalsType,
  ChallengeTargetAmountType,
  ChallengeTargetAmountTypeNullable,
  DailyStatusType,
  DueDateTargetType,
  EntityType,
  TARGET_PERIOD,
  TargetPeriodNullable,
  WeekdayTargetType,
  WeeklyTargetType,
} from 'features/challenge/config/types';
import { extractPromptFromTags } from 'features/challenge/utils';
import { DEFAULT_TOTAL_AMOUNT } from 'features/timeline/config';
import { DateRange } from 'features/timeline/config/types';

type WeekdayTargetProgressType = {
  type: typeof TARGET_TYPES.WEEKDAY;
  weekday: WeekdayTargetType;
  amount: ChallengeTargetAmountTypeNullable;
  period: TargetPeriodNullable;
};

type WeeklyTargetProgressType = {
  type: typeof TARGET_TYPES.WEEKLY;
  weekly: WeeklyTargetType;
  amount: ChallengeTargetAmountTypeNullable;
  period: TargetPeriodNullable;
};

export type WeekdayTargetProgressTypeWithAmount = {
  type: typeof TARGET_TYPES.WEEKDAY;
  weekday: WeekdayTargetType;
  amount: ChallengeTargetAmountType;
  period: TARGET_PERIOD;
};

export type WeeklyTargetProgressTypeWithAmount = {
  type: typeof TARGET_TYPES.WEEKLY;
  weekly: WeeklyTargetType;
  amount: ChallengeTargetAmountType;
  period: TARGET_PERIOD;
};

export type DueDateTargetProgressType = {
  type: typeof TARGET_TYPES.DUE_DATE;
  dueDate: DueDateTargetType;
};

export type RepeatedTargetType =
  | WeekdayTargetProgressType
  | WeeklyTargetProgressType
  | WeekdayTargetProgressTypeWithAmount
  | WeeklyTargetProgressTypeWithAmount;

export type NonRepeatedTargetType = DueDateTargetProgressType;

export type TargetType = RepeatedTargetType | NonRepeatedTargetType;

export interface IBaseChallengeProps {
  challengeId: string;
  completedDate: Nullable<string>;
  dailyStatuses: DailyStatusType;
  description: string;
  difficultyRating: ChallengeDifficultyRating;
  dimensions: string[];
  emoji: Nullable<string>;
  goals: ChallengeGoalsType;
  owner: Nullable<Owner>;
  passes?: number;
  picture: Nullable<Picture>;
  private: boolean;
  proofRequired: boolean;
  rank: string;
  reminder: Nullable<string>;
  states: {
    difficultyRating: ChallengeDifficultyRating;
    startDate: string;
    endDate: string;
    sprintId: Nullable<string>;
    target: TargetType;
    title?: string;
  }[];
  tags: string[];
  title: string;
  source: {
    curatedChallengeId: Nullable<string>;
    customChallengeId: Nullable<string>;
  };
  target: TargetType;
  sprintIds: string[];
}

export interface IRepeatedChallengeProps extends IBaseChallengeProps {
  states: {
    difficultyRating: ChallengeDifficultyRating;
    startDate: string;
    sprintId: Nullable<string>;
    endDate: string;
    target: RepeatedTargetType;
    title?: string;
  }[];
}

export interface INonRepeatedChallengeProps extends IBaseChallengeProps {
  states: {
    difficultyRating: ChallengeDifficultyRating;
    startDate: string;
    endDate: string;
    sprintId: Nullable<string>;
    target: NonRepeatedTargetType;
    title?: string;
  }[];
}

export interface IChallengeTimelineItem extends IBaseChallengeProps {
  target: TargetType;
  entityType: EntityType;
  type: typeof CHALLENGE_TYPES.REPEATING | typeof CHALLENGE_TYPES.NON_REPEATING;
}

type Owner = {
  name: string;
  picture: Nullable<Picture>;
  userId: string;
};

export abstract class BaseChallengeTimelineItem {
  readonly challengeId: string;
  readonly completedDate: Nullable<string>;
  dailyStatuses: DailyStatusType;
  description: string;
  difficultyRating: ChallengeDifficultyRating;
  dimensions: string[];
  emoji: Nullable<string>;
  goals: ChallengeGoalsType;
  readonly sprintIds: string[];
  readonly owner: Nullable<Owner>;
  passes: number;
  picture: Nullable<Picture>;
  private: boolean;
  entityType: EntityType;
  proofRequired: boolean;
  readonly rank: string;
  reminder: Nullable<string>;
  states: {
    difficultyRating: ChallengeDifficultyRating;
    startDate: string;
    sprintId: Nullable<string>;
    endDate: string;
    target: TargetType;
    title?: string;
  }[];
  tags: string[];
  target: TargetType;
  title: string;
  readonly source: {
    curatedChallengeId: Nullable<string>;
    customChallengeId: Nullable<string>;
  };

  constructor(challenge: IBaseChallengeProps) {
    this.completedDate = challenge.completedDate;
    this.description = challenge.description;
    this.dimensions = challenge.dimensions.map(firstLetterUpperCase);
    this.emoji = challenge.emoji;
    this.goals = challenge.goals;
    this.owner = challenge.owner;
    this.passes = challenge.passes || 0;
    this.picture = challenge.picture;
    this.source = challenge.source;
    this.tags = challenge.tags;
    this.title = challenge.title;
    this.private = challenge.private;
    this.proofRequired = challenge.proofRequired;
    this.rank = challenge.rank;
    this.reminder = challenge.reminder;
    this.challengeId = challenge.challengeId;
    this.dailyStatuses = challenge.dailyStatuses ?? {};
    this.source = challenge.source;
    this.states = challenge.states;
    this.target = challenge.states?.[0]?.target ?? challenge.target;
    this.difficultyRating =
      challenge.states?.[0]?.difficultyRating ?? challenge.difficultyRating;
    this.sprintIds = challenge.sprintIds;
    this.entityType = ENTITY_TYPES.CHALLENGE;
  }

  getTargetByDate(startDate: string, endDate: string): TargetType {
    const state = this.states.find(
      (state) =>
        dateUtils(state.startDate).isSameOrBefore(startDate) &&
        dateUtils(state.endDate).isSameOrAfter(endDate)
    );

    return (state?.target || this.target) as TargetType;
  }

  getTitleByDate(startDate: string, endDate: string): string {
    const state = this.states.find(
      (state) =>
        dateUtils(state.startDate).isSameOrBefore(startDate) &&
        (dateUtils(state.endDate).isSameOrAfter(endDate) || !state.endDate)
    );

    return state?.title || this.title;
  }

  getAmountOnCurrentDate(date: string): number {
    return this.dailyStatuses[date]?.amountTotal;
  }

  get availablePassesCount(): number {
    const usedPasses = Object.values(this.dailyStatuses).reduce(
      (sum, { status }) =>
        status === DAILY_STATUS_VALUES.PASS ? sum + 1 : sum,
      0
    );

    return this.passes ? this.passes - usedPasses : 0;
  }

  get isCuratedChallenge(): boolean {
    return Boolean(
      this.owner !== null && 'name' in this.owner && this.owner.name === 'hOS'
    );
  }

  abstract get isRepeating(): boolean;

  getEntityType({ startDate, endDate }: DateRange): EntityType {
    if (!this.states.length) {
      return ENTITY_TYPES.CHALLENGE;
    }

    let state;

    if (!startDate || !endDate) {
      state = this.states[this.states.length - 1];
    } else {
      state = this.states.find(
        (state) =>
          dateUtils(state.startDate).isSameOrBefore(startDate) &&
          dateUtils(state.endDate).isSameOrAfter(endDate)
      );
    }

    return state?.sprintId ? ENTITY_TYPES.CHALLENGE : ENTITY_TYPES.ROUTINE;
  }

  get customJournalPrompt(): string {
    return extractPromptFromTags(this.tags);
  }
}

export class RepeatedChallengeTimelineItem extends BaseChallengeTimelineItem {
  readonly type = CHALLENGE_TYPES.REPEATING;

  get isRepeating(): boolean {
    return true;
  }

  getIsFrequencyConfigurable(startDate?: string, endDate?: string): boolean {
    const target = (
      startDate && endDate
        ? this.getTargetByDate(startDate, endDate)
        : this.target
    ) as RepeatedTargetType;

    return !target.period || target.period === TARGET_PERIOD.PER_DAY;
  }

  get isIncrementalChallenge(): boolean {
    return 'amount' in this.target && !!this.target.amount;
  }

  private getWeeklyTargetAmountTotal({
    selectedWeek,
  }: {
    selectedWeek: string[];
  }): number {
    const dailyStatusDates = Object.keys(this.dailyStatuses);
    if (dailyStatusDates.length === 0) {
      return DEFAULT_TOTAL_AMOUNT;
    }

    const isWithinWeek = (date: string) => selectedWeek.includes(date);

    for (let i = dailyStatusDates.length - 1; i >= 0; i--) {
      const date = dailyStatusDates[i];

      if (isWithinWeek(date)) {
        const amountTotal = this.dailyStatuses[date]?.amountTotal;
        if (amountTotal !== null) {
          return amountTotal;
        }
      }
    }

    return DEFAULT_TOTAL_AMOUNT;
  }

  private getSprintTargetAmountTotal(): number {
    const dailyStatusDates = Object.keys(this.dailyStatuses || {});

    if (dailyStatusDates.length === 0) {
      return DEFAULT_TOTAL_AMOUNT;
    }

    for (let i = dailyStatusDates.length - 1; i >= 0; i--) {
      const date = dailyStatusDates[i];
      const amountTotal = this.dailyStatuses[date]?.amountTotal;

      if (amountTotal !== null) {
        return amountTotal;
      }
    }

    return DEFAULT_TOTAL_AMOUNT;
  }

  getTotalAmount(selectedWeek?: string[]): number {
    if (!('period' in this.target)) {
      return DEFAULT_TOTAL_AMOUNT;
    }

    switch (this.target.period) {
      case TARGET_PERIOD.PER_WEEK:
        return selectedWeek
          ? this.getWeeklyTargetAmountTotal({ selectedWeek })
          : DEFAULT_TOTAL_AMOUNT;
      case TARGET_PERIOD.PER_SPRINT:
        return this.getSprintTargetAmountTotal();
      default:
        return DEFAULT_TOTAL_AMOUNT;
    }
  }
}

export class NonRepeatedChallengeTimelineItem extends BaseChallengeTimelineItem {
  readonly type = CHALLENGE_TYPES.NON_REPEATING;

  get isRepeating(): boolean {
    return false;
  }

  get isIncrementalChallenge(): boolean {
    return false;
  }

  getIsFrequencyConfigurable(): boolean {
    return false;
  }

  getNonRepeatingChallengeStatus(): ObjectValues<typeof DAILY_STATUS_VALUES> {
    const statuses = Object.values(this.dailyStatuses || {});

    return (
      statuses.find(({ status }) => status !== DAILY_STATUS_VALUES.EMPTY)
        ?.status || DAILY_STATUS_VALUES.EMPTY
    );
  }
}

export type ChallengeTimelineItemType =
  | RepeatedChallengeTimelineItem
  | NonRepeatedChallengeTimelineItem;
